import React, { Suspense, lazy } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useAuth0 } from '../react-auth0-spa';
import Loading from 'components/loading';
import {
  ADD_POLICY_ROUTE,
  ADMIN_ROUTE,
  EDIT_POLICY_ROUTE,
  ADVICE_ROUTE,
  DASHBOARD_ROUTE,
  EXPLORE_ROUTE,
  HOME_ROUTE,
  INVITATION_ROUTE,
  LOGOUT_ROUTE,
  MARKET_SCAN_PURCHASE_ROUTE,
  MARKET_SCAN_RESULTS_LIFE_ROUTE,
  MARKET_SCAN_RESULTS_ROUTE,
  MARKET_SCAN_ROUTE,
  NOTIFICATIONS_ROUTE,
  OPERATIONS_ROUTE,
  POLICY_DETAILS_ROUTE,
  PROFILE_ROUTE,
  WINDCAVE_CALLBACK,
  EXTERNAL_PURCHASE_SUCCESS_ROUTE,
  WORKPLACE_OFFERS_ROUTE,
} from 'constants/routes';
import { UserRoles } from 'constants/user';

// Use React.lazy for lazy loading components
const UserRoute = lazy(() => import('containers/user'));
const NotificationRoute = lazy(() => import('containers/notifications'));
const AdminRoute = lazy(() => import('containers/admin'));
const OperationsRoute = lazy(() => import('containers/operations'));
const AddEditPolicyRoute = lazy(
  () => import('containers/add-edit-policy/AddEditPolicyContainer'),
);
const ExploreRoute = lazy(() => import('containers/explore'));
const GetAdviceRoute = lazy(() => import('containers/get-advice'));
const InvitationRoute = lazy(() => import('containers/invitation'));
const PoliciesRoute = lazy(() => import('containers/policies'));
const HomeRoute = lazy(() => import('containers/home'));
const MarketScanRoute = lazy(() => import('containers/market-scan'));
const MarketScanResultsRoute = lazy(
  () => import('containers/market-scan-results'),
);
const MarketScanResultsLifeRoute = lazy(
  () => import('containers/market-scan-results-life'),
);
const MarketScanPurchaseRoute = lazy(
  () => import('containers/market-scan-purchase'),
);
const PolicyDetailsRoute = lazy(() => import('containers/policy-details'));
const OnboardingRoute = lazy(() => import('containers/onboarding'));
const WorkplaceRoute = lazy(() => import('containers/workplace'));
const WindcaveCallbackRoute = lazy(
  () => import('containers/windcave-callback'),
);
const ExternalPurchaseSuccessRoute = lazy(
  () => import('containers/external-purchase'),
);
const LogoutRoute = lazy(() => import('containers/logout'));

export const OnboardingRoutes = () => (
  <Suspense fallback={<Loading full />}>
    <Switch>
      <Route path={LOGOUT_ROUTE} component={LogoutRoute} />
      <Route path={HOME_ROUTE} component={OnboardingRoute} />
    </Switch>
  </Suspense>
);

export const DefaultRoutes = () => {
  const { hasRole } = useAuth0();

  return (
    <Suspense fallback={<Loading full />}>
      <Switch>
        <Route path={LOGOUT_ROUTE} component={LogoutRoute} />
        <Route exact path={HOME_ROUTE} component={HomeRoute} />
        <Route path={DASHBOARD_ROUTE} component={PoliciesRoute} />
        <Route path={WINDCAVE_CALLBACK} component={WindcaveCallbackRoute} />
        <Route
          path={EXTERNAL_PURCHASE_SUCCESS_ROUTE}
          component={ExternalPurchaseSuccessRoute}
        />
        <Route path={WORKPLACE_OFFERS_ROUTE} component={WorkplaceRoute} />
        <Route path={PROFILE_ROUTE} render={() => <UserRoute />} />
        <Route path={NOTIFICATIONS_ROUTE} component={NotificationRoute} />
        <Route
          path={[ADD_POLICY_ROUTE, EDIT_POLICY_ROUTE]}
          component={AddEditPolicyRoute}
        />
        <Route path={ADVICE_ROUTE} component={GetAdviceRoute} />
        <Route path={EXPLORE_ROUTE} component={ExploreRoute} />
        <Route path={INVITATION_ROUTE} component={InvitationRoute} />
        <Route path={ADMIN_ROUTE}>
          {hasRole(UserRoles.ADMIN) ? <AdminRoute /> : <Redirect to="/" />}
        </Route>
        <Route
          path={OPERATIONS_ROUTE}
          render={() =>
            hasRole(UserRoles.ADMIN) || hasRole(UserRoles.SUPPORT) ? <OperationsRoute /> : <Redirect to="/" />
          }
        />
        <Route path={POLICY_DETAILS_ROUTE} component={PolicyDetailsRoute} />
        <Route
          path={MARKET_SCAN_PURCHASE_ROUTE}
          component={MarketScanPurchaseRoute}
        />
        <Route path={MARKET_SCAN_ROUTE} component={MarketScanRoute} />
        <Route
          path={MARKET_SCAN_RESULTS_LIFE_ROUTE}
          component={MarketScanResultsLifeRoute}
        />
        <Route
          path={MARKET_SCAN_RESULTS_ROUTE}
          component={MarketScanResultsRoute}
        />
        <Redirect to={HOME_ROUTE} />
      </Switch>
    </Suspense>
  );
};
